import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useCookPreferences } from '../../../hooks/useCookPreferences';
import { useReadableRecipeIndex } from '../../../hooks/useReadableRecipeIndex';
import { useToday } from '../../../hooks/useToday';
import { selectActiveInventory } from '../../../redux/inventorySlice';
import { selectCurrentLanguage } from '../../../redux/languageSlice';
import { isEntryMeal } from '../../../redux/mealEntry';
import type { PlannedMeal } from '../../../redux/mealPlanSlice';
import { selectAllRecipeMetadata } from '../../../redux/recipeMetadataSlice';
import { dailySeed } from '../../../services/fanSuggestionsService';
import {
  buildSideSuggestions,
  buildSidesWithFoods,
  sideFoodChoices,
  type SideAngleKey,
  type SideRecipe,
  type SideSuggestionsParams,
} from '../../../services/sideDish';
import type { RecipeIndexEntry } from '../../../services/unifiedRecipeService';
import { displayName } from '../../../utils/ingredientVocabulary';
import { resolveRecipeTitle } from '../../../utils/recipeTitle';
import { formatDishList } from '../../recipe-detail/planSheetLogic';
import { SIDE_REASONS } from './constants';
import { sideMainOf, sideMainRecipe } from './sideMain';

/** One accompaniment, ready to draw: titled in the reader's language, with its reason. */
export interface SideOption {
  angleKey: SideAngleKey;
  /** The recipe with `title` already resolved for the reader. */
  recipe: RecipeIndexEntry;
  reasonKey: string;
  reasonParams?: Record<string, string | number>;
  labelKey: string;
  accent: string;
}

/** A fresh food on the shelf the cook can ask the list to use (#853). */
export interface SideFoodChip {
  id: string;
  /** The food's name in the reader's language, capitalised for a chip. */
  label: string;
  /** Fresh rows of it on the shelf. */
  rows: number;
}

/** The list once foods are picked: one kind of row, no angles. */
export interface SidesWithFoods {
  options: Array<Pick<SideOption, 'recipe' | 'reasonKey' | 'reasonParams'>>;
  /** False when no side uses every picked food; `options` then holds those using some. */
  together: boolean;
  /** The picked foods as one phrase (« chou et radis »), for the line that says so. */
  foodsPhrase: string;
}

export interface SideSuggester {
  /**
   * The accompaniments for a slot's main dish — empty when the slot has no
   * recipe main or cannot take another dish.
   */
  forSlot: (slot: PlannedMeal[] | undefined, weekMeals: PlannedMeal[], seed: number) => SideOption[];
  /**
   * The one suggestion a card shows unasked. Seeded by the day and the dish,
   * so it is the same on every render and after a reload, and changes tomorrow.
   */
  topForSlot: (slot: PlannedMeal[] | undefined, weekMeals: PlannedMeal[]) => SideOption | null;
  /** The foods offered as chips above the list, the most plentiful first. */
  foodsForSlot: (slot: PlannedMeal[] | undefined) => SideFoodChip[];
  /** The sides that use the picked foods. */
  withFoodsForSlot: (slot: PlannedMeal[] | undefined, foods: string[]) => SidesWithFoods;
}

/**
 * The accompaniment list both planner layouts read (#746). It plans from the
 * library the reader may see (`useReadableRecipeIndex`) and under the cook's
 * hard rules — allergies, excluded ingredients, declared diets — like every
 * other suggestion list.
 */
export function useSideSuggestions(recipeIndex: Record<string, RecipeIndexEntry>): SideSuggester {
  const library = useReadableRecipeIndex();
  const language = useSelector(selectCurrentLanguage);
  const inventory = useSelector(selectActiveInventory);
  const metadataById = useSelector(selectAllRecipeMetadata);
  const prefs = useCookPreferences();
  const today = useToday();

  const lang = language === 'fr' ? 'fr' : 'en';
  const foodsPhrase = useCallback(
    (ids: readonly string[]) => formatDishList(ids.map((id) => displayName(id, lang)), language),
    [lang, language],
  );

  /** What every reader below asks the service, for one slot; null when it has no recipe main. */
  const paramsFor = useCallback((
    slot: PlannedMeal[] | undefined,
    weekMeals: PlannedMeal[],
    seed: number,
  ): SideSuggestionsParams<RecipeIndexEntry> | null => {
    const mainMeal = sideMainOf(slot);
    const main: SideRecipe | null = mainMeal ? sideMainRecipe(mainMeal, recipeIndex) : null;
    if (!mainMeal || !main) return null;
    return {
      main,
      allRecipes: library,
      slotRecipeIds: (slot ?? []).map((m) => m.recipeId),
      placedRecipeIds: weekMeals.map((m) => m.recipeId),
      inventory,
      metadataById,
      seed,
      today,
      exclude: prefs.hasHardRules ? prefs.isExcluded : undefined,
    };
  }, [recipeIndex, library, inventory, metadataById, prefs, today]);

  const forSlot = useCallback<SideSuggester['forSlot']>((slot, weekMeals, seed) => {
    const params = paramsFor(slot, weekMeals, seed);
    if (!params) return [];
    return buildSideSuggestions(params).map(({ foods, ...s }) => {
      const reason = SIDE_REASONS.find((r) => r.key === s.angleKey) ?? SIDE_REASONS[0];
      return {
        ...s,
        // The fridge reason names its foods (« concombre et laitue »), in the reader's language.
        ...(foods && {
          reasonParams: { ...s.reasonParams, foods: foodsPhrase(foods) },
        }),
        recipe: { ...s.recipe, title: resolveRecipeTitle(s.recipe, language) },
        labelKey: reason.labelKey,
        accent: reason.accent,
      };
    });
  }, [paramsFor, foodsPhrase, language]);

  const topForSlot = useCallback<SideSuggester['topForSlot']>((slot, weekMeals) => {
    const mainMeal = sideMainOf(slot);
    if (!mainMeal) return null;
    const dish = isEntryMeal(mainMeal) ? mainMeal.id : mainMeal.recipeId;
    return forSlot(slot, weekMeals, dailySeed(mainMeal.scheduledFor, dish))[0] ?? null;
  }, [forSlot]);

  const foodsForSlot = useCallback<SideSuggester['foodsForSlot']>((slot) => {
    const params = paramsFor(slot, [], 0);
    if (!params) return [];
    return sideFoodChoices(params).map(({ id, rows }) => {
      const name = displayName(id, lang);
      return { id, rows, label: name.charAt(0).toLocaleUpperCase(language) + name.slice(1) };
    });
  }, [paramsFor, lang, language]);

  const withFoodsForSlot = useCallback<SideSuggester['withFoodsForSlot']>((slot, foods) => {
    const params = paramsFor(slot, [], 0);
    if (!params || foods.length === 0) return { options: [], together: true, foodsPhrase: '' };
    const { matches, together } = buildSidesWithFoods({ ...params, foods });
    return {
      together,
      foodsPhrase: foodsPhrase(foods),
      options: matches.map((m) => ({
        recipe: { ...m.recipe, title: resolveRecipeTitle(m.recipe, language) },
        reasonKey: m.also.length > 0 ? 'sideFoods.withAlso' : 'sideFoods.with',
        reasonParams: { foods: foodsPhrase(m.foods), also: foodsPhrase(m.also) },
      })),
    };
  }, [paramsFor, foodsPhrase, language]);

  return { forSlot, topForSlot, foodsForSlot, withFoodsForSlot };
}
